import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo, useCallback, useLayoutEffect, useRef } from 'react';

import {
  type CoTToolInputProps,
  CoTToolInput,
  CoTReadFileTitle,
  CoTToolOutput,
  CoTToolSearchResults,
  hasReadFileReference,
  hasCoTToolInputContent,
  hasCoTToolOutputContent,
} from './chain-of-thought-renderers';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
  MessageResponse,
} from '../../ai-elements';
import {
  type CoTStep,
  getCoTStepIcon,
  getCoTStepKey,
  getCoTStepLabel,
  getCoTStepStatus,
  stripLeadingTitle,
} from '../lib/chain-of-thought-utils';
import type { SourcesMap } from '../lib/sources';

interface ChainOfThoughtBlockProps {
  steps: CoTStep[];
  allSteps?: CoTStep[];
  isWorking: boolean;
  stepProgressMap: Record<string, string>;
  sourcesMap?: SourcesMap;
  className?: string;
}

interface CompletedChainOfThoughtBlockProps {
  children: ReactNode;
  itemCount: number;
  allSteps: CoTStep[];
  title: string;
}

interface GroupedChainOfThoughtBlockProps {
  children: ReactNode;
  itemCount: number;
  icon: LucideIcon;
  title: ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// Shared step renderer
// ---------------------------------------------------------------------------

function renderStep(
  step: CoTStep,
  stepIndex: number,
  stepProgressMap: Record<string, string>,
  allSteps: CoTStep[],
  sourcesMap?: SourcesMap,
  hideLabel = false,
): ReactNode {
  const globalStepIndex = step.stepIndex ?? stepIndex;
  const stepKey = getCoTStepKey(step, globalStepIndex);
  const stepStatus = getCoTStepStatus(step);
  const stepProgress = step.type === 'tool' ? stepProgressMap[stepKey] : undefined;
  const priorSteps = allSteps.slice(0, globalStepIndex);
  const shouldRenderShellAsOutput =
    step.type === 'tool' && step.toolName === 'shell' && stepStatus === 'complete' && step.output != null;
  const shouldHideInputDescription =
    shouldRenderShellAsOutput ||
    (step.type === 'tool' &&
      (step.toolName === 'exposeFileToUser' || step.toolName === 'stageFileForShell') &&
      stepStatus === 'complete' &&
      step.output != null);
  const inputDescription =
    step.type === 'tool' && step.input != null && !shouldHideInputDescription ? (
      <CoTToolInput {...({ toolName: step.toolName, input: step.input, priorSteps } as CoTToolInputProps)} />
    ) : undefined;
  const readFileTitle =
    step.type === 'tool' &&
    step.toolName === 'readFile' &&
    step.input != null &&
    hasReadFileReference(step.input, priorSteps) ? (
      <CoTReadFileTitle input={step.input} priorSteps={priorSteps} />
    ) : null;
  const description =
    inputDescription && stepProgress ? (
      <>
        {inputDescription}
        <div>{stepProgress}</div>
      </>
    ) : (
      (inputDescription ?? stepProgress)
    );

  return (
    <ChainOfThoughtStep
      key={stepKey}
      label={hideLabel ? null : (readFileTitle ?? getCoTStepLabel(step, sourcesMap))}
      description={description}
      status={stepStatus}
    >
      {step.type === 'reasoning' && step.text && (
        <MessageResponse className="text-muted-foreground">
          {step.title ? stripLeadingTitle(step.text) : step.text}
        </MessageResponse>
      )}
      {step.type === 'tool' && stepStatus === 'complete' && step.output != null && (
        <>
          <CoTToolSearchResults toolName={step.toolName} output={step.output} sourcesMap={sourcesMap} />
          <div className="text-muted-foreground size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <CoTToolOutput toolName={step.toolName} input={step.input} output={step.output} />
          </div>
        </>
      )}
    </ChainOfThoughtStep>
  );
}

function hasRenderableStepBody(step: CoTStep, stepIndex: number, allSteps: CoTStep[]): boolean {
  if (step.type === 'reasoning') {
    return !!(step.title ? stripLeadingTitle(step.text) : step.text).trim();
  }

  const globalStepIndex = step.stepIndex ?? stepIndex;
  const priorSteps = allSteps.slice(0, globalStepIndex);
  const stepStatus = getCoTStepStatus(step);
  const shouldRenderShellAsOutput = step.toolName === 'shell' && stepStatus === 'complete' && step.output != null;
  const shouldHideInputContent =
    shouldRenderShellAsOutput ||
    ((step.toolName === 'exposeFileToUser' || step.toolName === 'stageFileForShell') &&
      stepStatus === 'complete' &&
      step.output != null);
  const hasInputContent =
    step.input != null &&
    !shouldHideInputContent &&
    hasCoTToolInputContent({ toolName: step.toolName, input: step.input, priorSteps });
  const hasOutputContent =
    stepStatus === 'complete' &&
    step.output != null &&
    hasCoTToolOutputContent({ toolName: step.toolName, input: step.input, output: step.output });

  return hasInputContent || hasOutputContent;
}

// ---------------------------------------------------------------------------
// useStableHeight — prevents container from shrinking during streaming
// ---------------------------------------------------------------------------

function useStableHeight(isStreaming: boolean, resetKey: string) {
  const ref = useRef<HTMLDivElement>(null);
  const maxHeightRef = useRef(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    maxHeightRef.current = 0;
    el.style.minHeight = '';
  }, [resetKey]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (isStreaming) {
      const height = el.getBoundingClientRect().height;
      if (height > maxHeightRef.current) {
        maxHeightRef.current = height;
      }
      el.style.minHeight = `${maxHeightRef.current}px`;
    } else {
      el.style.minHeight = '';
      maxHeightRef.current = 0;
    }
  });

  return ref;
}

// ---------------------------------------------------------------------------
// ChainOfThoughtBlock
// ---------------------------------------------------------------------------

export const ChainOfThoughtBlock = memo(
  ({ steps, allSteps = steps, isWorking, stepProgressMap, sourcesMap, className }: ChainOfThoughtBlockProps) => {
    const currentStep = steps[steps.length - 1];
    const currentStepKey = getCoTStepKey(currentStep, currentStep.stepIndex ?? steps.length - 1);
    const stableRef = useStableHeight(isWorking, currentStepKey);
    const analytics = useAnalytics();
    const blockTitle = getChainOfThoughtBlockTitle(steps, sourcesMap, allSteps);
    const BlockIcon = getChainOfThoughtBlockIcon(steps);
    const hasContent = steps.some((step, i) => hasRenderableStepBody(step, i, allSteps));

    const handlePreviousStepsToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Previous Steps Toggled',
          properties: { open, stepCount: steps.length },
        });
      },
      [analytics, steps.length],
    );

    const handleHeaderToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Toggled',
          properties: { open, stepCount: steps.length },
        });
      },
      [analytics, steps.length],
    );

    if (isWorking) {
      return (
        <div ref={stableRef}>
          <ChainOfThought
            isStreaming
            defaultOpen
            className={className}
            data-testid="chain-of-thought-block"
            onOpenChange={handlePreviousStepsToggle}
          >
            <ChainOfThoughtHeader icon={BlockIcon}>{blockTitle}</ChainOfThoughtHeader>
            <ChainOfThoughtContent showConnector={false} className="mt-2">
              <div className="flex flex-col gap-3">
                {steps.map((step, i) =>
                  renderStep(step, i, stepProgressMap, allSteps, sourcesMap, i === steps.length - 1),
                )}
              </div>
            </ChainOfThoughtContent>
          </ChainOfThought>
        </div>
      );
    }

    // After streaming: standard collapsible with header
    if (!hasContent) {
      return (
        <ChainOfThought isStreaming={false} className={className} data-testid="chain-of-thought-block">
          <ChainOfThoughtHeader collapsible={false} icon={BlockIcon}>
            {blockTitle}
          </ChainOfThoughtHeader>
        </ChainOfThought>
      );
    }

    return (
      <ChainOfThought
        isStreaming={false}
        className={className}
        data-testid="chain-of-thought-block"
        onOpenChange={handleHeaderToggle}
      >
        <ChainOfThoughtHeader icon={BlockIcon}>{blockTitle}</ChainOfThoughtHeader>
        <ChainOfThoughtContent showConnector={false} className="mt-2">
          {steps.map((step, i) => renderStep(step, i, stepProgressMap, allSteps, sourcesMap, steps.length === 1))}
        </ChainOfThoughtContent>
      </ChainOfThought>
    );
  },
);

ChainOfThoughtBlock.displayName = 'ChainOfThoughtBlock';

function getChainOfThoughtBlockTitle(
  steps: CoTStep[],
  sourcesMap?: SourcesMap,
  allSteps: CoTStep[] = steps,
): ReactNode {
  const currentStep = steps[steps.length - 1];

  if (currentStep?.type === 'tool') {
    if (currentStep.toolName === 'readFile' && currentStep.input != null) {
      const stepIndex = currentStep.stepIndex ?? allSteps.indexOf(currentStep);
      const priorSteps = stepIndex >= 0 ? allSteps.slice(0, stepIndex) : [];
      if (hasReadFileReference(currentStep.input, priorSteps)) {
        return <CoTReadFileTitle input={currentStep.input} priorSteps={priorSteps} />;
      }
    }
  }

  return currentStep ? getCoTStepLabel(currentStep, sourcesMap) : 'Thinking...';
}

function getChainOfThoughtBlockIcon(steps: CoTStep[]): LucideIcon {
  return getCoTStepIcon(steps[steps.length - 1]);
}

export const CompletedChainOfThoughtBlock = memo(
  ({ children, itemCount, allSteps, title }: CompletedChainOfThoughtBlockProps) => {
    const analytics = useAnalytics();

    const handleHeaderToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Toggled',
          properties: { open, stepCount: allSteps.length, itemCount },
        });
      },
      [analytics, allSteps.length, itemCount],
    );

    return (
      <ChainOfThought
        isStreaming={false}
        data-testid="completed-chain-of-thought-block"
        onOpenChange={handleHeaderToggle}
      >
        <ChainOfThoughtHeader>{title}</ChainOfThoughtHeader>
        <ChainOfThoughtContent showConnector={false} className="mt-2">
          <div className="pb-2" role="separator">
            <div className="border-border/70 border-t" />
          </div>
          <div className="flex flex-col gap-3">{children}</div>
          <div className="pt-2 pb-1" role="separator">
            <div className="border-border/70 border-t" />
          </div>
        </ChainOfThoughtContent>
      </ChainOfThought>
    );
  },
);

CompletedChainOfThoughtBlock.displayName = 'CompletedChainOfThoughtBlock';

export const GroupedChainOfThoughtBlock = memo(
  ({ children, itemCount, icon, title, className }: GroupedChainOfThoughtBlockProps) => {
    const analytics = useAnalytics();

    const handleHeaderToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Group Toggled',
          properties: { open, itemCount },
        });
      },
      [analytics, itemCount],
    );

    return (
      <ChainOfThought
        isStreaming={false}
        className={className}
        data-testid="grouped-chain-of-thought-block"
        onOpenChange={handleHeaderToggle}
      >
        <ChainOfThoughtHeader icon={icon}>{title}</ChainOfThoughtHeader>
        <ChainOfThoughtContent showConnector={false} className="mt-2">
          <div className="border-border/70 ml-3 flex flex-col gap-3 border-l pl-6">{children}</div>
        </ChainOfThoughtContent>
      </ChainOfThought>
    );
  },
);

GroupedChainOfThoughtBlock.displayName = 'GroupedChainOfThoughtBlock';
